<script setup>
import { computed, nextTick, reactive, ref } from 'vue'
import { capitalize, clock, dayMonth, kr, shortDate, weekday } from '../lib/format'

const props = defineProps({
  bakeDay: { type: Object, required: true },
  lines: { type: Array, required: true },
  totalOre: { type: Number, required: true },
})
const emit = defineEmits(['placed', 'reset', 'stale'])

const form = reactive({ name: '', email: '', phone: '', note: '', website: '' })
const fieldErrors = reactive({ name: null, email: null, phone: null })
const formEl = ref(null)
const submitting = ref(false)
const error = ref(null)
const placed = ref(null) // snapshot of the confirmed order
const openedAt = new Date().toISOString()

const pickupLabel = computed(
  () => `${capitalize(weekday(props.bakeDay.pickupStartsAt))} ${dayMonth(props.bakeDay.pickupStartsAt)}`,
)
const pickupTime = computed(() => `${clock(props.bakeDay.pickupStartsAt)}–${clock(props.bakeDay.pickupEndsAt)}`)
const label = (product) => [product.name, product.unit].filter(Boolean).join(' ')

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const PHONE = /^\+?[\d\s()-]{6,}$/

function validateField(field) {
  const value = form[field].trim()
  const messages = {
    name: !value && 'Skriv dit navn',
    email: !EMAIL.test(value) && (value ? 'Tjek din e-mail – fx navn@mail.dk' : 'Skriv din e-mail'),
    phone: value && !PHONE.test(value) && 'Kun tal, fx 12 34 56 78',
  }
  fieldErrors[field] = messages[field] || null
  return !fieldErrors[field]
}

async function validate() {
  const results = ['name', 'email', 'phone'].map(validateField)
  if (results.every(Boolean)) return true

  await nextTick()
  formEl.value?.querySelector('[aria-invalid="true"]')?.focus()
  return false
}

// Decorative barcode: bar widths derived from a string so it stays stable per order.
function bars(seed) {
  let hash = [...seed].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7)
  const out = []
  let x = 0
  while (x < 196) {
    hash = (hash * 1103515245 + 12345) >>> 0
    const width = 1 + (hash % 3)
    if ((hash >> 4) % 3) out.push({ x, width })
    x += width + 1 + ((hash >> 8) % 2)
  }
  return out
}

async function submit() {
  if (submitting.value || props.lines.length === 0) return
  error.value = null
  if (!(await validate())) return

  submitting.value = true
  const token = document.querySelector('meta[name="csrf-token"]')?.content
  try {
    const response = await fetch('/api/orders', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': token },
      body: JSON.stringify({
        order: {
          bake_day_id: props.bakeDay.id,
          ...form,
          items: props.lines.map((line) => ({ product_id: line.product.id, quantity: line.quantity })),
        },
      }),
    })
    const data = await response.json().catch(() => ({}))

    if (response.ok) {
      placed.value = {
        ...data.order,
        email: form.email.trim(),
        name: form.name.trim(),
        lines: props.lines.map((line) => ({ ...line })),
        bakeDay: props.bakeDay,
      }
      Object.assign(form, { note: '', website: '' })
      emit('placed')
    } else {
      error.value = data.error || 'Noget gik galt. Prøv igen.'
      if (data.storefront) emit('stale', data.storefront)
    }
  } catch {
    error.value = 'Ingen forbindelse. Tjek dit internet og prøv igen.'
  } finally {
    submitting.value = false
  }
}

function startOver() {
  placed.value = null
  emit('reset')
}
</script>

<template>
  <div class="receipt-wrap">
    <!-- Confirmed order -->
    <article v-if="placed" class="receipt receipt--placed" aria-live="polite">
      <header class="receipt__head">
        <p class="receipt__brand">den salte klejne</p>
        <p class="receipt__sub">forudbestilling fra et mikrobageri</p>
      </header>
      <hr class="receipt__rule" />
      <dl class="receipt__meta">
        <div><dt>Bestilling</dt><dd>#{{ placed.number }}</dd></div>
        <div><dt>Modtaget</dt><dd>{{ shortDate(placed.createdAt) }} {{ clock(placed.createdAt) }}</dd></div>
        <div><dt>Navn</dt><dd>{{ placed.name }}</dd></div>
        <div>
          <dt>Afhentning</dt>
          <dd>{{ capitalize(weekday(placed.bakeDay.pickupStartsAt)) }} {{ dayMonth(placed.bakeDay.pickupStartsAt) }}</dd>
        </div>
        <div><dt></dt><dd>kl. {{ clock(placed.bakeDay.pickupStartsAt) }}–{{ clock(placed.bakeDay.pickupEndsAt) }}</dd></div>
        <div v-if="placed.pickupLocation"><dt>Sted</dt><dd>{{ placed.pickupLocation }}</dd></div>
      </dl>
      <hr class="receipt__rule" />
      <ul class="receipt__lines">
        <li v-for="line in placed.lines" :key="line.product.id">
          <span>{{ line.quantity }} × {{ label(line.product) }}</span>
          <span>{{ kr(line.totalOre) }}</span>
        </li>
      </ul>
      <hr class="receipt__rule receipt__rule--double" />
      <p class="receipt__total"><span>I alt</span><span>DKK {{ kr(placed.totalOre) }}</span></p>
      <p class="receipt__small">Betales ved afhentning</p>
      <hr class="receipt__rule" />
      <p class="receipt__stamp">Reserveret</p>
      <p class="receipt__small receipt__center">
        En kopi er sendt til<br />{{ placed.email }}
      </p>
      <svg class="receipt__barcode" viewBox="0 0 200 36" preserveAspectRatio="none" aria-hidden="true">
        <rect v-for="(bar, i) in bars(placed.token)" :key="i" :x="bar.x" y="0" :width="bar.width" height="36" />
      </svg>
      <p class="receipt__thanks">Tak! Vi glæder os til at se dig</p>
      <div class="receipt__after">
        <a :href="placed.url">Se din bestilling</a>
        <button type="button" class="linkish" @click="startOver">Bestil mere</button>
      </div>
    </article>

    <!-- Basket / order form -->
    <form v-else ref="formEl" class="receipt" novalidate @submit.prevent="submit">
      <header class="receipt__head">
        <p class="receipt__brand">den salte klejne</p>
        <p class="receipt__sub">forudbestilling fra et mikrobageri</p>
      </header>
      <hr class="receipt__rule" />
      <dl class="receipt__meta">
        <div><dt>Dato</dt><dd>{{ shortDate(openedAt) }}</dd></div>
        <div><dt>Afhentning</dt><dd>{{ pickupLabel }}</dd></div>
        <div><dt></dt><dd>kl. {{ pickupTime }}</dd></div>
      </dl>
      <hr class="receipt__rule" />

      <p v-if="lines.length === 0" class="receipt__empty">
        Kurven er tom<br />
        <span>Vælg noget fra dagens bagværk</span>
      </p>
      <TransitionGroup v-else name="line" tag="ul" class="receipt__lines">
        <li v-for="line in lines" :key="line.product.id">
          <span>{{ line.quantity }} × {{ label(line.product) }}</span>
          <span>{{ kr(line.totalOre) }}</span>
        </li>
      </TransitionGroup>

      <hr class="receipt__rule receipt__rule--double" />
      <p class="receipt__total"><span>I alt</span><span>DKK {{ kr(totalOre) }}</span></p>
      <p class="receipt__small">Betales ved afhentning</p>
      <hr class="receipt__rule" />

      <fieldset class="receipt__fields" :disabled="submitting">
        <legend class="receipt__legend">Dine oplysninger</legend>
        <div class="field" :class="{ 'has-error': fieldErrors.name }">
          <label for="order-name">Navn</label>
          <input
            id="order-name"
            v-model="form.name"
            name="name"
            autocomplete="name"
            maxlength="80"
            :aria-invalid="!!fieldErrors.name"
            aria-describedby="order-name-error"
            @blur="form.name && validateField('name')"
          />
          <p v-if="fieldErrors.name" id="order-name-error" class="field__error">{{ fieldErrors.name }}</p>
        </div>
        <div class="field" :class="{ 'has-error': fieldErrors.email }">
          <label for="order-email">E-mail</label>
          <input
            id="order-email"
            v-model="form.email"
            name="email"
            type="email"
            inputmode="email"
            autocomplete="email"
            autocapitalize="off"
            spellcheck="false"
            maxlength="120"
            :aria-invalid="!!fieldErrors.email"
            aria-describedby="order-email-error"
            @blur="form.email && validateField('email')"
          />
          <p v-if="fieldErrors.email" id="order-email-error" class="field__error">{{ fieldErrors.email }}</p>
        </div>
        <div class="field" :class="{ 'has-error': fieldErrors.phone }">
          <label for="order-phone">Telefon <span>valgfri</span></label>
          <input
            id="order-phone"
            v-model="form.phone"
            name="phone"
            type="tel"
            inputmode="tel"
            autocomplete="tel"
            maxlength="30"
            :aria-invalid="!!fieldErrors.phone"
            aria-describedby="order-phone-error"
            @blur="validateField('phone')"
          />
          <p v-if="fieldErrors.phone" id="order-phone-error" class="field__error">{{ fieldErrors.phone }}</p>
        </div>
        <div class="field">
          <label for="order-note">Note <span>valgfri</span></label>
          <textarea id="order-note" v-model="form.note" name="note" rows="2" maxlength="500" />
        </div>
        <div class="field field--trap" aria-hidden="true">
          <label for="order-website">Website</label>
          <input id="order-website" v-model="form.website" name="website" tabindex="-1" autocomplete="off" />
        </div>
      </fieldset>

      <p v-if="error" class="receipt__error" role="alert">{{ error }}</p>

      <button type="submit" class="receipt__submit" :disabled="submitting || lines.length === 0">
        <span v-if="submitting">Lægger til side…</span>
        <span v-else-if="lines.length === 0">Vælg først en vare</span>
        <span v-else>Bestil for DKK {{ kr(totalOre) }}</span>
      </button>
      <p class="receipt__thanks">Tak fordi du handler lokalt</p>
    </form>
  </div>
</template>
